import React, { useState, useRef } from 'react';
import { firstWordUppercase, openWiseConnectPage } from '../../../utils';
import { useSnackbarContext } from '../../../contexts/Snackbar';
import { useDialogsContext } from '../../../contexts/Dialog';
import { BankAccount, BankType } from '../../../Types';
import { deleteBankAccount, reconnectPlaidAccount } from '../../API';
import { CustomTooltip } from '../../BuildingBlocks/Tooltip/Tooltip';
import { ReferableConnectButton as ConnectBankAccount } from '../../Bank/ConnectBank';
import cloneDeep from 'lodash.clonedeep';
import companyInfo from '../../../themes/themes';
import messages from '../../../messages';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import DeleteBank from '../../Bank/DeleteBank';
import ReconnectBank from '../../Bank/ReconnectPlaid';
import Status from '../../BuildingBlocks/Misc/Status';

const useStyles = makeStyles((theme: any) => {
  const tableMinWidth = theme.spacing(110);
  return {
    tableHeader: {
      display: 'flex',
      flexWrap: 'nowrap',
      justifyContent: 'space-between',
      fontWeight: 700,
      fontSize: theme.spacing(2),
      height: theme.spacing(5),
      paddingRight: theme.spacing(4),
      paddingLeft: theme.spacing(4),
      borderBottom: `1px solid rgba(224, 224, 224, 1)`,
      minWidth: tableMinWidth + theme.spacing(4) + theme.spacing(4),
    },
    content: {
      paddingRight: theme.spacing(4),
      paddingLeft: theme.spacing(4),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(4),
    },
    borderRadius: {
      borderRadius: theme.spacing(1.5),
    },
    tableRow: {
      display: 'flex',
      flexWrap: 'nowrap',
      justifyContent: 'space-between',
      background: '#FFF',
      border: `1px solid ${theme.palette.brand.greyWhite}`,
      width: '100%',
      height: theme.spacing(6),
      minWidth: tableMinWidth,
      borderRadius: theme.spacing(1),
      '&:not(:first-child)': {
        marginTop: theme.spacing(1),
      },
    },
    tableCell: {
      display: 'flex',
      justifyContent: 'center',
      whiteSpace: 'nowrap',
      textAlign: 'center',
      marginTop: 'auto',
      marginBottom: 'auto',
    },
    header: {
      display: 'block',
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
      textAlign: 'center',
      marginTop: 'auto',
      marginBottom: 'auto',
    },
    bankContainer: {
      maxWidth: theme.spacing(22),
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    noBanksNote: {
      height: theme.spacing(6),
      width: '100%',
      backgroundColor: theme.palette.primary.light,
      border: `1px solid ${theme.palette.brand.blueSky}`,
      color: theme.palette.primary.main,
      borderRadius: theme.spacing(1),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    buttonConatainer: { display: 'flex' },
    connectButton: { marginTop: theme.spacing(4) },
    noPadding: { padding: 0 },
  };
});

const headers = {
  bank: {
    id: 'bank',
    label: 'Bank',
    width: 15,
  },
  account_name: {
    id: 'account_name',
    label: 'Account name',
    width: 15,
  },
  account_number: {
    id: 'account_number',
    label: 'Account number',
    width: 15,
  },
  type: {
    id: 'type',
    label: 'Type',
    width: 15,
  },
  ach_status: {
    id: 'ach_status',
    label: 'ACH Status',
    width: 15,
  },
  connect_status: {
    id: 'connect_status',
    label: 'Status',
    width: 15,
  },
  actions: {
    id: 'actions',
    label: '',
    width: 5,
  },
};

export default function BankAccounts({
  bankAccounts,
  setBankAccounts,
  plaidLinkToken,
  setLoading,
}) {
  const theme = process.env.REACT_APP_THEME;

  const classes = useStyles();

  const dialogs: any = useDialogsContext();

  const snackbar = useSnackbarContext();

  const [bankToDelete, setBankToDelete] = useState(null);
  const connectAccountButton = useRef<HTMLDivElement>(null);

  function addNewBankToState(newBankAccount: BankAccount) {
    let newAccountIsPrimary = false;
    let filteredBankAccounts = bankAccounts;
    const {
      linkedContractAccountId,
      id: newAccountId,
      bank,
      account,
      accountId,
      accountType,
      accountNumber,
    } = newBankAccount;
    filteredBankAccounts = filteredBankAccounts.filter((bankAccount) => {
      if (bankAccount.id === linkedContractAccountId) {
        newAccountIsPrimary = bankAccount.primaryAccount;
        return false;
      } else if (bankAccount.id === newAccountId) {
        return false;
      }
      return true;
    });
    setBankAccounts([
      ...filteredBankAccounts,
      {
        id: newAccountId,
        bank,
        account,
        accountId,
        accountType,
        accountNumber,
        error: false,
        hasToken: true,
        primaryAccount: newAccountIsPrimary,
      },
    ]);
  }

  const [bankToReconnect, setBankToReconnect] = useState(null);
  const [plaidLinkTokenToReconnect, setPlaidLinkTokenToReconnect] =
    useState(null);

  async function handlePlaidReconnect(account: BankAccount) {
    setLoading(true);
    try {
      const plaidRes = await reconnectPlaidAccount(account?.id);
      setPlaidLinkTokenToReconnect(plaidRes.link_token);
      setBankToReconnect(account);
    } catch (error) {
      snackbar.show(messages.bank.errors.misssingLinkToken, 'error');
    }
    setLoading(false);
  }

  async function handleWiseReconnect() {
    setLoading(true);
    try {
      await openWiseConnectPage();
    } catch (error) {
      snackbar.show(messages.bank.errors.wiseRedirect, 'error');
    }
    setLoading(false);
  }

  async function handleReconnectBank(account: BankAccount) {
    if (account.tokenType === BankType.plaid) {
      handlePlaidReconnect(account);
    }
    if (account.tokenType === BankType.wise) {
      handleWiseReconnect();
    }
  }

  const errorsRequiringReconnect = [
    'ITEM_LOGIN_REQUIRED',
    'INVALID_UPDATED_USERNAME',
  ];

  function renderCell(account, headerId: string) {
    switch (headerId) {
      case headers.bank.id:
        return (
          <div className={classes.bankContainer}>
            <CustomTooltip
              arrow
              title={account.bank}
              placement="top"
              enterDelay={500}
              enterNextDelay={500}
              disableHoverListener={account.bank ? false : true}
            >
              <span>{account.bank ? account.bank : '\u2014'}</span>
            </CustomTooltip>
          </div>
        );
      case headers.account_name.id:
        return (
          <div className={classes.bankContainer}>
            <CustomTooltip
              arrow
              title={account.account}
              placement="top"
              enterDelay={500}
              enterNextDelay={500}
              disableHoverListener={account.account ? false : true}
            >
              <span>{account.account ? account.account : '\u2014'}</span>
            </CustomTooltip>
          </div>
        );
      case headers.account_number.id:
        return account.accountNumber ? account.accountNumber : '\u2014';
      case headers.type.id:
        return account.accountType
          ? firstWordUppercase(account.accountType)
          : '\u2014';
      case headers.ach_status.id:
        return account.primaryAccount ? (
          <Status label="Enabled" status="achEnabled" />
        ) : (
          '-'
        );
      case headers.connect_status.id:
        let errorType = 'ATTENTION_REQUIRED';
        let errorMessage = `Please contact ${companyInfo[theme].company} support for help fixing this issue.`;
        if (account.error) {
          if (errorsRequiringReconnect.includes(account.error)) {
            errorType = 'RECONNECT_REQUIRED';
            errorMessage = 'Please reconnect your bank account.';
          }
          if (account.error === 'MFA_NOT_SUPPORTED') {
            errorMessage =
              'Please disable Multi-Factor Authentication. It is not supported for use with Plaid, the service we use to connect to your bank.';
          }
        }
        return account.error ? (
          <CustomTooltip
            arrow
            title={<Typography>{errorMessage}</Typography>}
            placement="top"
          >
            <div>
              {errorType === 'ATTENTION_REQUIRED' && (
                <Status status="attentionRequired" label="Attention Required" />
              )}
              {errorType === 'RECONNECT_REQUIRED' && (
                <Status
                  status="reconnect"
                  label="Reconnect"
                  onClick={() => handleReconnectBank(account)}
                />
              )}
            </div>
          </CustomTooltip>
        ) : (
          <>
            {account.hasToken ? (
              <Status status="connected" />
            ) : (
              <Status
                status="notConnected"
                label="Not Connected"
                onClick={() => connectAccountButton.current?.click()}
              />
            )}
          </>
        );
      case headers.actions.id:
        return (
          <div className={classes.buttonConatainer}>
            <CustomTooltip
              arrow
              title="Delete"
              placement="top"
              enterDelay={500}
              enterNextDelay={500}
            >
              <IconButton
                color="primary"
                className={classes.noPadding}
                onClick={() => {
                  setBankToDelete(account);
                  dialogs.open('deleteBank');
                }}
              >
                <img src="/icons/trashCanOutlined.svg" alt="" />
              </IconButton>
            </CustomTooltip>
          </div>
        );
      default:
        return null;
    }
  }

  return (
    <>
      <div className={classes.tableHeader}>
        {Object.values(headers).map((header, i) => (
          <div
            key={i}
            className={classes.header}
            style={{ width: `${header.width}%` }}
          >
            {header.label}
          </div>
        ))}
      </div>

      <div className={classes.content}>
        {bankAccounts.length === 0 && (
          <div className={classes.noBanksNote}>
            <Typography>
              No bank account connected. Connect an account to obtain funds for
              your supply chain.
            </Typography>
          </div>
        )}
        {bankAccounts.length > 0 && (
          <>
            {bankAccounts.map((account, i) => (
              <div key={i} className={classes.tableRow}>
                {Object.values(headers).map((header, i) => {
                  const { width } = header;
                  return (
                    <div
                      key={i}
                      className={classes.tableCell}
                      style={{ width: `${width}%` }}
                    >
                      {renderCell(account, header.id)}
                    </div>
                  );
                })}
              </div>
            ))}
          </>
        )}
        <div className={classes.connectButton}>
          <ConnectBankAccount
            ref={connectAccountButton}
            buttonVariant="contained"
            extraClasses={classes.borderRadius}
            buttonColor="primary"
            plaidLinkToken={plaidLinkToken}
            onSuccess={addNewBankToState}
          />
        </div>
        {bankToReconnect && plaidLinkTokenToReconnect && (
          <ReconnectBank
            plaidLinkToken={plaidLinkTokenToReconnect}
            account={bankToReconnect}
            onExit={() => {
              setBankToReconnect(null);
              setPlaidLinkTokenToReconnect(null);
            }}
            onSuccess={() => {
              const bankAccountsCopy = cloneDeep(bankAccounts);
              const updatedBankAccounts = bankAccountsCopy.map((account) => {
                if (account.id === bankToReconnect.id) {
                  account['error'] = null;
                }
                return account;
              });
              setBankAccounts(updatedBankAccounts);
              setBankToReconnect(null);
              setPlaidLinkTokenToReconnect(null);
            }}
          />
        )}
        {bankToDelete && (
          <DeleteBank
            open={dialogs.deleteBank}
            handleClose={() => dialogs.close('deleteBank')}
            bank={bankToDelete}
            onSubmit={async () => {
              try {
                await deleteBankAccount(bankToDelete.id);
                setBankAccounts(
                  bankAccounts.filter((bank) => bank.id !== bankToDelete.id),
                );
              } catch (e) {
                const knownErrorTypes = [
                  'NOT_EXISTING_BANK_ACCOUNT_DELETION_ERROR',
                  'CONTRACT_BANK_ACCOUNT_DELETION_ERROR',
                  'PRIMARY_BANK_ACCOUNT_DELETION_ERROR',
                ];
                const message = knownErrorTypes.includes(e?.error)
                  ? e.message
                  : messages.bank.errors.deletionFailure;
                snackbar.show(message, 'error');
              }
            }}
          />
        )}
      </div>
    </>
  );
}
